import type { TextParagraph, TextRun } from '../model';

// #756 -- pure edits on a standalone text annotation's paragraphs/runs. Positions are offsets into
// the block's plain text, where each paragraph break counts as one character ('\n'), so a DOM
// selection maps to a [start, end) pair and back without knowing about runs.

export type RunFormat = Omit<TextRun, 'text'>;
export type Alignment = NonNullable<TextParagraph['align']>;
// `null` clears a property; `undefined`/absent leaves it alone.
export type FormatPatch = { readonly [K in keyof RunFormat]?: RunFormat[K] | null };

type Paragraphs = readonly TextParagraph[];

const FORMAT_KEYS = ['fontFamily', 'fontSize', 'bold', 'italic', 'script'] as const;

// The canonical stored form: false/absent are the same, so false is dropped.
function canonicalFormat(format: RunFormat): RunFormat {
  const out: { -readonly [K in keyof RunFormat]?: RunFormat[K] } = {};
  if (format.fontFamily !== undefined) out.fontFamily = format.fontFamily;
  if (format.fontSize !== undefined) out.fontSize = format.fontSize;
  if (format.bold) out.bold = true;
  if (format.italic) out.italic = true;
  if (format.script !== undefined) out.script = format.script;
  return out;
}

export function formatOf(run: TextRun): RunFormat {
  const { text: _text, ...format } = run;
  return canonicalFormat(format);
}

function sameFormat(a: RunFormat, b: RunFormat): boolean {
  const left = canonicalFormat(a);
  const right = canonicalFormat(b);
  return FORMAT_KEYS.every((key) => left[key] === right[key]);
}

function run(text: string, format: RunFormat): TextRun {
  return { text, ...canonicalFormat(format) };
}

function paragraphLength(paragraph: TextParagraph): number {
  return paragraph.runs.reduce((sum, entry) => sum + entry.text.length, 0);
}

export function textLength(paragraphs: Paragraphs): number {
  return paragraphs.reduce((sum, paragraph) => sum + paragraphLength(paragraph), 0) + paragraphs.length - 1;
}

export function isEmptyText(paragraphs: Paragraphs): boolean {
  return paragraphs.every((paragraph) => paragraph.runs.length === 0);
}

function paragraph(runs: readonly TextRun[], align: TextParagraph['align']): TextParagraph {
  return { ...(align && align !== 'left' ? { align } : {}), runs };
}

// Merges neighbouring runs with equal formatting and drops empty runs; keeps at least one
// paragraph.
export function normalizeParagraphs(paragraphs: Paragraphs): TextParagraph[] {
  const result = paragraphs.map((entry) => {
    const runs: TextRun[] = [];
    for (const candidate of entry.runs) {
      if (!candidate.text) continue;
      const previous = runs.at(-1);
      if (previous && sameFormat(formatOf(previous), formatOf(candidate))) {
        runs[runs.length - 1] = run(previous.text + candidate.text, formatOf(previous));
      } else {
        runs.push(run(candidate.text, formatOf(candidate)));
      }
    }
    return paragraph(runs, entry.align);
  });
  return result.length > 0 ? result : [{ runs: [] }];
}

// Paragraph index and in-paragraph offset of a block offset, clamped to the text.
export function locate(paragraphs: Paragraphs, offset: number): { readonly index: number; readonly offset: number } {
  let remaining = Math.max(0, offset);
  for (let index = 0; index < paragraphs.length; index++) {
    const length = paragraphLength(paragraphs[index]!);
    if (remaining <= length) return { index, offset: remaining };
    remaining -= length + 1;
  }
  const last = paragraphs.length - 1;
  return { index: last, offset: paragraphLength(paragraphs[last]!) };
}

export function blockOffset(paragraphs: Paragraphs, index: number, offset: number): number {
  let total = 0;
  for (let i = 0; i < index; i++) total += paragraphLength(paragraphs[i]!) + 1;
  return total + offset;
}

function splitRuns(runs: readonly TextRun[], at: number): [TextRun[], TextRun[]] {
  const left: TextRun[] = [];
  const right: TextRun[] = [];
  let position = 0;
  for (const entry of runs) {
    const end = position + entry.text.length;
    if (end <= at) left.push(entry);
    else if (position >= at) right.push(entry);
    else {
      left.push(run(entry.text.slice(0, at - position), formatOf(entry)));
      right.push(run(entry.text.slice(at - position), formatOf(entry)));
    }
    position = end;
  }
  return [left, right];
}

function ordered(start: number, end: number): [number, number] {
  return start <= end ? [start, end] : [end, start];
}

// Replaces [start, end) with `text` ('\n' starts a new paragraph) typed in `format`. New
// paragraphs inherit the alignment of the paragraph the edit started in.
export function replaceRange(
  paragraphs: Paragraphs,
  start: number,
  end: number,
  text: string,
  format: RunFormat
): TextParagraph[] {
  const [from, to] = ordered(start, end);
  const head = locate(paragraphs, from);
  const tail = locate(paragraphs, to);
  const startParagraph = paragraphs[head.index]!;
  const [before] = splitRuns(startParagraph.runs, head.offset);
  const [, after] = splitRuns(paragraphs[tail.index]!.runs, tail.offset);
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const inserted = lines.map((line, index) => {
    const runs = [
      ...(index === 0 ? before : []),
      ...(line ? [run(line, format)] : []),
      ...(index === lines.length - 1 ? after : [])
    ];
    return paragraph(runs, startParagraph.align);
  });
  return normalizeParagraphs([...paragraphs.slice(0, head.index), ...inserted, ...paragraphs.slice(tail.index + 1)]);
}

function patchFormat(format: RunFormat, patch: FormatPatch): RunFormat {
  const next: Record<string, unknown> = { ...format };
  for (const key of FORMAT_KEYS) {
    if (!(key in patch) || patch[key] === undefined) continue;
    if (patch[key] === null) delete next[key];
    else next[key] = patch[key];
  }
  return canonicalFormat(next as RunFormat);
}

// Applies `patch` to every character in [start, end); characters outside keep their runs.
export function formatRange(paragraphs: Paragraphs, start: number, end: number, patch: FormatPatch): TextParagraph[] {
  const [from, to] = ordered(start, end);
  if (from === to) return [...paragraphs];
  return normalizeParagraphs(
    paragraphs.map((entry, index) => {
      const paragraphStart = blockOffset(paragraphs, index, 0);
      const localFrom = Math.max(0, from - paragraphStart);
      const localTo = Math.min(paragraphLength(entry), to - paragraphStart);
      if (localFrom >= localTo) return entry;
      const [left, rest] = splitRuns(entry.runs, localFrom);
      const [middle, right] = splitRuns(rest, localTo - localFrom);
      return paragraph(
        [...left, ...middle.map((candidate) => run(candidate.text, patchFormat(formatOf(candidate), patch))), ...right],
        entry.align
      );
    })
  );
}

// Sets the alignment of every paragraph the range touches (the caret's paragraph when collapsed).
export function alignRange(paragraphs: Paragraphs, start: number, end: number, align: Alignment): TextParagraph[] {
  const [from, to] = ordered(start, end);
  const first = locate(paragraphs, from).index;
  const last = locate(paragraphs, to).index;
  return paragraphs.map((entry, index) => (index >= first && index <= last ? paragraph(entry.runs, align) : entry));
}

// The format new typing takes at a caret: the character before it in the same paragraph, else the
// paragraph's first run, else the end of the nearest earlier non-empty paragraph.
export function formatAt(paragraphs: Paragraphs, offset: number): RunFormat {
  const { index, offset: local } = locate(paragraphs, offset);
  const entry = paragraphs[index]!;
  let position = 0;
  for (const candidate of entry.runs) {
    position += candidate.text.length;
    if (local > 0 && local <= position) return formatOf(candidate);
  }
  if (entry.runs[0]) return formatOf(entry.runs[0]);
  for (let i = index - 1; i >= 0; i--) {
    const lastRun = paragraphs[i]!.runs.at(-1);
    if (lastRun) return formatOf(lastRun);
  }
  return {};
}

export interface RangeFormat {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly script: RunFormat['script'] | 'mixed';
  readonly fontFamily: string | undefined | 'mixed';
  readonly fontSize: number | undefined | 'mixed';
  readonly align: Alignment | 'mixed';
}

// What the formatting controls show for [start, end): a toggle is on only when every selected
// character has it; differing values are 'mixed'. A collapsed range reports the caret's format.
export function rangeFormat(paragraphs: Paragraphs, start: number, end: number): RangeFormat {
  const [from, to] = ordered(start, end);
  const formats: RunFormat[] = [];
  if (from === to) formats.push(formatAt(paragraphs, from));
  else {
    paragraphs.forEach((entry, index) => {
      const paragraphStart = blockOffset(paragraphs, index, 0);
      let position = paragraphStart;
      for (const candidate of entry.runs) {
        const runEnd = position + candidate.text.length;
        if (runEnd > from && position < to) formats.push(formatOf(candidate));
        position = runEnd;
      }
    });
    if (formats.length === 0) formats.push(formatAt(paragraphs, from));
  }
  const shared = <K extends 'script' | 'fontFamily' | 'fontSize'>(key: K) => {
    const first = formats[0]![key];
    return formats.every((format) => format[key] === first) ? first : 'mixed';
  };
  const first = locate(paragraphs, from).index;
  const last = locate(paragraphs, to).index;
  const aligns = paragraphs.slice(first, last + 1).map((entry) => entry.align ?? 'left');
  return {
    bold: formats.every((format) => format.bold === true),
    italic: formats.every((format) => format.italic === true),
    script: shared('script'),
    fontFamily: shared('fontFamily'),
    fontSize: shared('fontSize'),
    align: aligns.every((align) => align === aligns[0]) ? aligns[0]! : 'mixed'
  };
}

export function emptyParagraphs(): TextParagraph[] {
  return [{ runs: [] }];
}
